import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsAPI } from '../api/vendly';
import ProductCard from '../components/ProductCard';
import styles from './ShopPage.module.css';

const ALL_CATS = [
  { id: 'all',         label: 'All',          icon: '🛍️' },
  { id: 'electronics', label: 'Electronics',  icon: '📱' },
  { id: 'fashion',     label: 'Fashion',      icon: '👗' },
  { id: 'home',        label: 'Home & Living',icon: '🏠' },
  { id: 'beauty',      label: 'Beauty',       icon: '💄' },
  { id: 'sports',      label: 'Sports',       icon: '⚽' },
  { id: 'gaming',      label: 'Gaming',       icon: '🎮' },
  { id: 'computing',   label: 'Computing',    icon: '💻' },
  { id: 'supermarket', label: 'Supermarket',  icon: '🛒' },
  { id: 'foodstuff',   label: 'Foodstuff',    icon: '🍔' },
];

const SORT_OPTIONS = [
  { value: 'featured',   label: '⭐ Featured'       },
  { value: 'newest',     label: '🆕 Newest first'   },
  { value: 'price_asc',  label: '💰 Price: Low → High' },
  { value: 'price_desc', label: '💰 Price: High → Low' },
  { value: 'rating',     label: '⭐ Top rated'      },
];

const PRICE_RANGES = [
  { label: 'Any price',    min: null, max: null  },
  { label: 'Under ₦5k',   min: null, max: 5000  },
  { label: '₦5k – ₦20k',  min: 5000, max: 20000 },
  { label: '₦20k – ₦50k', min: 20000, max: 50000 },
  { label: 'Over ₦50k',   min: 50000, max: null  },
];

export default function ShopPage() {
  const [params] = useSearchParams();
  const [sort,      setSort]      = useState('featured');
  const [sortOpen,  setSortOpen]  = useState(false);
  const [activeCat, setActiveCat] = useState(params.get('cat') || 'all');
  const [catOpen,   setCatOpen]   = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categories, setCategories] = useState(ALL_CATS);
  const [priceRange, setPriceRange] = useState(0); // index into PRICE_RANGES
  const [minRating,  setMinRating]  = useState(0); // 0 = any
  const filterRef = useRef(null);

  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);

  const qParam = params.get('q') || '';

  // Close filter on outside click
  useEffect(() => {
    const h = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    productsAPI.categories()
      .then(res => {
        const totalAll = res.data.categories.reduce((s, c) => s + c.count, 0);
        setCategories(ALL_CATS.map(c => {
          if (c.id === 'all') return { ...c, count: totalAll };
          const found = res.data.categories.find(r => r.id === c.id);
          return { ...c, count: found?.count || 0 };
        }));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    const range = PRICE_RANGES[priceRange];
    productsAPI.list({
      page,
      per_page: 20,
      sort,
      ...(activeCat !== 'all' && { category: activeCat }),
      ...(qParam     && { q: qParam }),
      ...(range.min  && { min_price: range.min }),
      ...(range.max  && { max_price: range.max }),
      ...(minRating  && { min_rating: minRating }),
    })
      .then(res => {
        setProducts(res.data.products);
        setTotalPages(res.data.pages);
        setTotal(res.data.total);
      })
      .catch(() => setError('Failed to load products. Is the backend running?'))
      .finally(() => setLoading(false));
  }, [activeCat, sort, page, qParam, priceRange, minRating]);

  const handleCat  = (id)  => { setActiveCat(id); setPage(1); setCatOpen(false); };
  const handleSort = (val) => { setSort(val); setPage(1); setSortOpen(false); };
  const activeFiltersCount = (priceRange > 0 ? 1 : 0) + (minRating > 0 ? 1 : 0);

  const activeCatObj   = categories.find(c => c.id === activeCat);
  const activeSortObj  = SORT_OPTIONS.find(s => s.value === sort);

  return (
    <div className={styles.page}>
      <div className={styles.layout}>

        {/* Sidebar — desktop only */}
        <aside className={styles.sidebar}>
          <h3 className={styles.sideTitle}>Categories</h3>
          {categories.map(c => (
            <button
              key={c.id}
              className={`${styles.catBtn} ${activeCat === c.id ? styles.catActive : ''}`}
              onClick={() => handleCat(c.id)}
            >
              <span>{c.icon}</span>
              <span className={styles.catBtnLabel}>{c.label}</span>
              {c.count > 0 && <span className={styles.catCount}>{c.count}</span>}
            </button>
          ))}

          <div className={styles.sideSection}>
            <h3 className={styles.sideTitle}>Price range</h3>
            {PRICE_RANGES.map((r, i) => (
              <button
                key={i}
                className={`${styles.filterChip} ${priceRange === i ? styles.filterChipActive : ''}`}
                onClick={() => { setPriceRange(i); setPage(1); }}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className={styles.sideSection}>
            <h3 className={styles.sideTitle}>Min rating</h3>
            <div className={styles.ratingBtns}>
              {[0,3,4,5].map(r => (
                <button
                  key={r}
                  className={`${styles.filterChip} ${minRating === r ? styles.filterChipActive : ''}`}
                  onClick={() => { setMinRating(r); setPage(1); }}
                >
                  {r === 0 ? 'Any' : `${r}★ +`}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className={styles.main}>

          {/* Mobile: categories dropdown */}
          <div className={styles.mobileCatWrap}>
            <button className={styles.mobileCatBtn} onClick={() => setCatOpen(o => !o)}>
              <span>{activeCatObj?.icon} {activeCatObj?.label || 'All'}</span>
              <svg className={`${styles.chevron} ${catOpen ? styles.chevronOpen : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <div className={`${styles.mobileCatList} ${catOpen ? styles.mobileCatListOpen : ''}`}>
              <div>
                {categories.map(c => (
                  <button
                    key={c.id}
                    className={`${styles.mobileCatItem} ${activeCat === c.id ? styles.mobileCatItemActive : ''}`}
                    onClick={() => handleCat(c.id)}
                  >
                    <span>{c.icon} {c.label}</span>
                    {c.count > 0 && <span className={styles.catCount}>{c.count}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Result count + active filters */}
          {!error && (
            <div className={styles.resultRow}>
              <p className={styles.resultCount}>{loading ? 'Loading…' : `${total} product${total !== 1 ? 's' : ''} found`}</p>
              {activeFiltersCount > 0 && (
                <button className={styles.clearFilters} onClick={() => { setPriceRange(0); setMinRating(0); setPage(1); }}>
                  Clear filters ({activeFiltersCount})
                </button>
              )}
            </div>
          )}
          {error && <div className={styles.errorBanner}>{error}</div>}

          {/* Products grid */}
          {loading ? (
            <div className={styles.grid}>
              {Array.from({ length: 10 }).map((_, i) => <div key={i} className={styles.skeleton} />)}
            </div>
          ) : products.length > 0 ? (
            <div className={styles.grid}>
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className={styles.empty}>
              <span>🔍</span>
              <p>No products found{qParam ? ` for "${qParam}"` : ''}</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button key={n} className={`${styles.pageBtn} ${n === page ? styles.pageBtnActive : ''}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </main>
      </div>

      {/* Floating sort button */}
      <div className={styles.floatSort}>
        <button className={styles.floatSortBtn} onClick={() => setSortOpen(o => !o)}>
          <span className={styles.sortLines}>
            <span style={{ width: 18 }} />
            <span style={{ width: 13 }} />
            <span style={{ width: 9 }} />
          </span>
          <span className={styles.floatSortLabel}>{activeSortObj?.label || 'Sort'}</span>
        </button>
        {sortOpen && (
          <div className={styles.sortMenu}>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`${styles.sortItem} ${sort === opt.value ? styles.sortItemActive : ''}`}
                onClick={() => handleSort(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
